import { inject, Injectable } from '@angular/core';
import {
    applyNotificationPropertiesSnapshot,
    entryToNotificationModel,
    NotificationIdState,
    NotificationModel,
    selectNotificationAddressesById,
    selectNotificationEntryById,
    selectNotificationPropertiesSnapshotById,
} from '@evorait/pace-core';
import { Store } from '@ngrx/store';

/**
 * Builds the `NotificationModel` that the model-based collaborators of the notification-details
 * screen still require — the modal models, the escalation and ETA-alert timers, the software-licence
 * load — straight from the notification store.
 *
 * The store's own converters do the work; this only pairs them up and reads the two selectors they
 * need. Keeping it in one place means retiring such a collaborator is a matter of deleting its call
 * here, and that no consumer can accidentally hand out a model missing the fields the
 * the store records for the notification.
 */
@Injectable({ providedIn: 'root' })
export class NotificationModelFactory {
    private readonly store = inject(Store);

    /**
     * @param id the notification the model is built for
     * @returns the model, or `undefined` when the store holds no such notification
     */
    public create(id: NotificationIdState): NotificationModel | undefined {
        const entry = this.store.selectSignal(selectNotificationEntryById(id))();
        const addresses = this.store.selectSignal(selectNotificationAddressesById(id))();
        if (!entry || !addresses) {
            return undefined;
        }
        const snapshot = this.store.selectSignal(selectNotificationPropertiesSnapshotById(id))();
        return applyNotificationPropertiesSnapshot(
            entryToNotificationModel(entry, addresses.customer, addresses.addressSet),
            snapshot,
        );
    }
}
